import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { carregarDadosCasoPortal, urlAssinadaArquivoCaso } from '../lib/casoPortal';
import { COR_TIPO_PRAZO, ETAPAS_ADM, ETAPAS_JUDICIAL, FAQ_MANUAL, ALERTA_SEGURANCA } from '../lib/manualCliente';
import '../styles/portal.css';

const ETAPAS_CHECKLIST = [
  { campo: 'dt_contrato_assinado',           label: 'Contrato assinado' },
  { campo: 'dt_pagamento_realizado',         label: 'Pagamento realizado' },
  { campo: 'dt_procuracao_hipossuficiencia', label: 'Procuração e hipossuficiência enviada' },
  { campo: 'dt_documentos_entregues',        label: 'Documentos entregues' },
  { campo: 'dt_notificacao_enviada',         label: 'Notificação enviada' },
  { campo: 'dt_acao_protocolada',            label: 'Ação protocolada' },
  { campo: 'dt_impugnacao',                  label: 'Impugnação' },
  { campo: 'dt_sentenca',                    label: 'Sentença' },
];

const STATUS_DOC = {
  pendente: { label: 'Pendente', bg: '#fef3c7', cor: '#92400e' },
  enviado:  { label: 'Enviado',  bg: '#dbeafe', cor: '#1d4ed8' },
  assinado: { label: 'Assinado', bg: '#dcfce7', cor: '#16a34a' },
};

const CATEGORIA_ARQUIVO_INFO = {
  contrato:    { label: 'Contratos', icone: '📄' },
  comprovante: { label: 'Comprovantes', icone: '🧾' },
};

function iniciais(nome) { return (nome || '?').trim().slice(0, 1).toUpperCase(); }
function fmtMoeda(v) { return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); }
function fmtData(d) { return d ? new Date(d + 'T00:00').toLocaleDateString('pt-BR') : '—'; }
function fmtDataHora(iso) { return iso ? new Date(iso).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—'; }
function diasAtraso(dataStr) {
  if (!dataStr) return 0;
  const hojeStr = new Date().toISOString().split('T')[0];
  return Math.max(0, Math.floor((new Date(hojeStr) - new Date(dataStr)) / 86400000));
}

export default function CasoPortal() {
  const { token } = useParams();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState('caso');
  const [abrindoId, setAbrindoId] = useState(null);
  const [manualAberto, setManualAberto] = useState(false);
  const [fluxoManual, setFluxoManual] = useState('adm');

  async function abrirArquivo(a) {
    setAbrindoId(a.id);
    try {
      const url = await urlAssinadaArquivoCaso(a.storage_path);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      alert('Não consegui abrir esse arquivo agora. Tente de novo em instantes.');
    }
    setAbrindoId(null);
  }

  useEffect(() => {
    carregarDadosCasoPortal(token)
      .then(setDados)
      .catch(e => setErro(e.message || 'Não foi possível carregar este caso.'))
      .finally(() => setCarregando(false));
  }, [token]);

  if (carregando) {
    return <div className="portal-page"><div className="portal-estado">Carregando...</div></div>;
  }

  if (erro || !dados) {
    return (
      <div className="portal-page">
        <div className="portal-estado">
          <div style={{ fontSize: 32, marginBottom: 10 }}>🔒</div>
          Este link não é válido. Confirme o endereço com o escritório que está cuidando do seu caso.
        </div>
      </div>
    );
  }

  const { escritorio, caso, cobrancas, documentos, arquivos, atualizacoes } = dados;
  const cor = escritorio?.cor || '#7B2FBE';
  // etapas ocultas pela equipe (prazos/decisões internas) nunca aparecem aqui
  const etapasVisiveis = ETAPAS_CHECKLIST.filter(e => !(caso.etapas_ocultas_cliente || []).includes(e.campo));
  const totalEtapas = etapasVisiveis.length + (caso.etapas_extra || []).length;
  const concluidas = etapasVisiveis.filter(e => caso[e.campo]).length + (caso.etapas_extra || []).filter(e => e.data).length;
  const cobsAtrasadas = (cobrancas || []).filter(c => c.status !== 'Recebido' && diasAtraso(c.data_prevista) > 0);

  return (
    <div className="portal-page">
      <div className="portal-header" style={{ background: `linear-gradient(135deg, ${cor}, ${cor}cc)` }}>
        {escritorio?.logo_url ? (
          <img src={escritorio.logo_url} alt={escritorio.nome} style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', flexShrink: 0, background: 'white' }} />
        ) : (
          <div className="logo-mark" style={{ background: 'rgba(255,255,255,0.2)', width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 700, flexShrink: 0 }}>
            {iniciais(escritorio?.nome)}
          </div>
        )}
        <div className="portal-header-texto">
          <strong>{escritorio?.nome || 'Seu escritório'}</strong>
          <small>Acompanhamento do seu caso</small>
        </div>
      </div>

      <div className="portal-body">
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'Playfair Display, serif' }}>Olá, {caso.nome_cliente}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 4 }}>{caso.status || 'Em andamento'}</div>
        </div>

        <div className="portal-tabs tabs-wrap">
          <button onClick={() => setAba('caso')} className={`tab-btn${aba === 'caso' ? ' active' : ''}`}>Meu Caso</button>
          <button onClick={() => setAba('atualizacoes')} className={`tab-btn${aba === 'atualizacoes' ? ' active' : ''}`}>
            Atualizações {atualizacoes?.length > 0 ? `(${atualizacoes.length})` : ''}
          </button>
          <button onClick={() => setAba('financeiro')} className={`tab-btn${aba === 'financeiro' ? ' active' : ''}`}>Financeiro</button>
          <button onClick={() => setAba('documentos')} className={`tab-btn${aba === 'documentos' ? ' active' : ''}`}>
            Documentos {documentos?.length > 0 ? `(${documentos.filter(d => d.status !== 'pendente').length}/${documentos.length})` : ''}
          </button>
          <button onClick={() => setAba('arquivos')} className={`tab-btn${aba === 'arquivos' ? ' active' : ''}`}>
            Meus Arquivos {arquivos?.length > 0 ? `(${arquivos.length})` : ''}
          </button>
        </div>

        {aba === 'caso' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <button onClick={() => setManualAberto(!manualAberto)}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 20 }}>📖</span>
                  <span>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Como funciona o seu processo</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 1 }}>Passo a passo completo, prazos e perguntas frequentes</div>
                  </span>
                </span>
                <span style={{ color: cor, fontSize: 13 }}>{manualAberto ? '▲' : '▼'}</span>
              </button>

              {manualAberto && (
                <div style={{ padding: '0 18px 20px' }}>
                  {(escritorio?.advogado_nome || escritorio?.advogado_bio) && (
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: '#F3EBFF', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                      <div style={{ width: 34, height: 34, borderRadius: '50%', background: cor, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0, fontFamily: 'Playfair Display, serif' }}>
                        {iniciais(escritorio.advogado_nome || escritorio.nome)}
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: cor, marginBottom: 2 }}>Quem vai cuidar do seu caso</div>
                        {escritorio.advogado_nome && <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{escritorio.advogado_nome}{escritorio.oab ? ` · ${escritorio.oab}` : ''}</div>}
                        {escritorio.advogado_bio && <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 3 }}>{escritorio.advogado_bio}</div>}
                      </div>
                    </div>
                  )}

                  <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 14 }}>
                    Preparamos este material pra você acompanhar, com total transparência, todas as etapas do seu processo. Sempre começamos pelo caminho <b>Administrativo</b> — buscando um acordo direto, sem processo judicial — e só seguimos pro <b>Judicial</b> se não houver acordo no prazo. Guarde este material: pode consultar sempre que quiser saber em que etapa está.
                  </p>

                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14, fontSize: 11 }}>
                    {Object.entries(COR_TIPO_PRAZO).map(([k, v]) => (
                      <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: v.cor, display: 'inline-block' }} />
                        {v.label}
                      </span>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                    <button onClick={() => setFluxoManual('adm')}
                      style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: `1.5px solid ${fluxoManual === 'adm' ? cor : 'var(--border)'}`, background: fluxoManual === 'adm' ? cor : 'transparent', color: fluxoManual === 'adm' ? 'white' : 'var(--text-secondary)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      Caminho Administrativo
                    </button>
                    <button onClick={() => setFluxoManual('judicial')}
                      style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: `1.5px solid ${fluxoManual === 'judicial' ? cor : 'var(--border)'}`, background: fluxoManual === 'judicial' ? cor : 'transparent', color: fluxoManual === 'judicial' ? 'white' : 'var(--text-secondary)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      Caminho Judicial
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                    {(fluxoManual === 'adm' ? ETAPAS_ADM : ETAPAS_JUDICIAL).map(e => {
                      const info = COR_TIPO_PRAZO[e.tipo];
                      return (
                        <div key={e.n} style={{ display: 'flex', gap: 10, padding: '10px 12px', background: info.bg, borderRadius: 9, borderLeft: `3px solid ${info.cor}` }}>
                          <div style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 15, color: info.cor, width: 20, flexShrink: 0 }}>{e.n}</div>
                          <div>
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>
                              {e.titulo} {e.prazo && <span style={{ fontWeight: 400, color: info.cor, fontSize: 11 }}>· {e.prazo}</span>}
                            </div>
                            <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 2 }}>{e.texto}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Perguntas frequentes</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                    {FAQ_MANUAL.map((f, i) => (
                      <div key={i}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{f.p}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 2 }}>{f.r}</div>
                      </div>
                    ))}
                  </div>

                  {(escritorio?.contato_whatsapp || escritorio?.contato_whatsapp_atendimento || escritorio?.contato_email || escritorio?.oab) && (
                    <div style={{ background: '#F3EBFF', borderRadius: 9, padding: '12px 14px', fontSize: 12, marginBottom: 14 }}>
                      <div style={{ fontWeight: 700, color: cor, marginBottom: 4 }}>Fale com a gente</div>
                      {escritorio.contato_whatsapp && <div style={{ color: 'var(--text-primary)' }}>WhatsApp (pós-venda): {escritorio.contato_whatsapp}</div>}
                      {escritorio.contato_whatsapp_atendimento && <div style={{ color: 'var(--text-primary)' }}>WhatsApp (atendimento): {escritorio.contato_whatsapp_atendimento}</div>}
                      {escritorio.contato_email && <div style={{ color: 'var(--text-primary)' }}>E-mail: {escritorio.contato_email}</div>}
                      {escritorio.oab && <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 4 }}>{escritorio.oab}</div>}
                    </div>
                  )}

                  <div style={{ background: '#fee2e2', border: '1px solid #dc2626', borderRadius: 9, padding: '12px 14px', fontSize: 12 }}>
                    <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>{ALERTA_SEGURANCA.titulo}</div>
                    <ul style={{ margin: 0, paddingLeft: 18, color: '#7a1f1f', lineHeight: 1.6 }}>
                      {ALERTA_SEGURANCA.itens.map((t, i) => <li key={i} style={{ marginBottom: i < ALERTA_SEGURANCA.itens.length - 1 ? 6 : 0 }}>{t}</li>)}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {(caso.tipos_acao || []).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {caso.tipos_acao.map(t => (
                  <span key={t} style={{ background: '#F3EBFF', color: cor, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20 }}>{t}</span>
                ))}
              </div>
            )}

            {caso.resumo && (
              <div className="card" style={{ padding: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase' }}>Sobre o seu caso</div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{caso.resumo}</div>
              </div>
            )}

            <div className="card" style={{ padding: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase' }}>Andamento</div>
              <div style={{ height: 4, background: 'var(--bg-secondary)', borderRadius: 4, marginBottom: 12 }}>
                <div style={{ width: `${totalEtapas ? (concluidas / totalEtapas) * 100 : 0}%`, height: '100%', background: cor, borderRadius: 4 }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {etapasVisiveis.map(e => (
                  <div key={e.campo} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <span style={{ color: caso[e.campo] ? '#16a34a' : 'var(--gray-300)' }}>{caso[e.campo] ? '✓' : '○'}</span>
                    <span style={{ color: caso[e.campo] ? 'var(--text-primary)' : 'var(--gray-400)', fontWeight: caso[e.campo] ? 600 : 400, flex: 1 }}>{e.label}</span>
                    {caso[e.campo] && <span style={{ color: 'var(--gray-400)', fontSize: 11 }}>{fmtData(caso[e.campo])}</span>}
                  </div>
                ))}
                {(caso.etapas_extra || []).map((e, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <span style={{ color: e.data ? '#16a34a' : 'var(--gray-300)' }}>{e.data ? '✓' : '○'}</span>
                    <span style={{ color: e.data ? 'var(--text-primary)' : 'var(--gray-400)', fontWeight: e.data ? 600 : 400, flex: 1 }}>{e.label}</span>
                    {e.data && <span style={{ color: 'var(--gray-400)', fontSize: 11 }}>{fmtData(e.data)}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {aba === 'atualizacoes' && (
          <div className="card" style={{ overflow: 'hidden' }}>
            {(!atualizacoes || atualizacoes.length === 0) && (
              <div className="portal-estado" style={{ padding: 30 }}>Ainda não há atualizações — assim que houver novidade (ou a cada 15 dias durante as tratativas), aparece aqui.</div>
            )}
            {atualizacoes?.map((a, i) => (
              <div key={a.id} style={{ display: 'flex', gap: 12, padding: '14px 18px', borderBottom: i < atualizacoes.length - 1 ? '1px solid rgba(107,33,168,0.06)' : 'none' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: cor, marginTop: 5, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, marginBottom: 3 }}>{fmtDataHora(a.created_at)}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{a.texto}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {aba === 'financeiro' && (
          <div className="card" style={{ overflow: 'hidden' }}>
            {(!cobrancas || cobrancas.length === 0) && <div className="portal-estado" style={{ padding: 30 }}>Nenhum lançamento financeiro ainda.</div>}
            {cobrancas?.map((c, i) => {
              const dias = c.status !== 'Recebido' ? diasAtraso(c.data_prevista) : 0;
              const atrasado = dias > 0;
              return (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: i < cobrancas.length - 1 ? '1px solid rgba(107,33,168,0.06)' : 'none', background: atrasado ? '#fff8f8' : 'transparent', borderLeft: atrasado ? '3px solid #dc2626' : '3px solid transparent' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.descricao}</div>
                    <div style={{ fontSize: 11, color: atrasado ? '#dc2626' : 'var(--text-secondary)', marginTop: 2, fontWeight: atrasado ? 600 : 400 }}>
                      {c.status === 'Recebido' ? `Recebido em ${fmtData(c.data_recebimento)}` : atrasado ? `Venceu em ${fmtData(c.data_prevista)} · ${dias} dia${dias > 1 ? 's' : ''} em atraso` : `Previsto para ${fmtData(c.data_prevista)}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: cor, fontFamily: 'Playfair Display, serif' }}>{fmtMoeda(c.valor_total)}</div>
                    <span style={{ background: c.status === 'Recebido' ? '#dcfce7' : atrasado ? '#fee2e2' : '#fef3c7', color: c.status === 'Recebido' ? '#16a34a' : atrasado ? '#dc2626' : '#92400e', padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>
                      {c.status === 'Recebido' ? 'Recebido' : atrasado ? '⚠ Em atraso' : 'Pendente'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {aba === 'documentos' && (
          <div className="card" style={{ overflow: 'hidden' }}>
            {(!documentos || documentos.length === 0) && <div className="portal-estado" style={{ padding: 30 }}>Nenhum documento na lista ainda.</div>}
            {documentos?.map((d, i) => {
              const s = STATUS_DOC[d.status] || STATUS_DOC.pendente;
              return (
                <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px', borderBottom: i < documentos.length - 1 ? '1px solid rgba(107,33,168,0.06)' : 'none' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{d.nome}</div>
                    {d.observacao && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{d.observacao}</div>}
                  </div>
                  <span style={{ background: s.bg, color: s.cor, padding: '3px 11px', borderRadius: 20, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{s.label}</span>
                </div>
              );
            })}
          </div>
        )}

        {aba === 'arquivos' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(!arquivos || arquivos.length === 0) && (
              <div className="card"><div className="portal-estado" style={{ padding: 30 }}>Nenhum arquivo disponível ainda — assim que o escritório enviar o contrato ou algum comprovante, aparece aqui.</div></div>
            )}
            {['contrato', 'comprovante'].map(cat => {
              const itens = (arquivos || []).filter(a => a.categoria === cat);
              if (itens.length === 0) return null;
              const info = CATEGORIA_ARQUIVO_INFO[cat];
              return (
                <div key={cat}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase' }}>{info.icone} {info.label}</div>
                  <div className="card" style={{ overflow: 'hidden' }}>
                    {itens.map((a, i) => (
                      <div key={a.id} onClick={() => abrirArquivo(a)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderBottom: i < itens.length - 1 ? '1px solid rgba(107,33,168,0.06)' : 'none', cursor: 'pointer' }}>
                        <span style={{ fontSize: 16 }}>{info.icone}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: cor }}>{a.nome}</div>
                          {a.descricao && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{a.descricao}</div>}
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{abrindoId === a.id ? 'abrindo...' : '⬇ abrir'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {cobsAtrasadas.length > 0 && aba !== 'financeiro' && (
          <div style={{ marginTop: 16, background: '#fee2e2', border: '1px solid #dc2626', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
            ⚠ Você tem {cobsAtrasadas.length} pagamento{cobsAtrasadas.length > 1 ? 's' : ''} em atraso — veja a aba Financeiro.
          </div>
        )}
      </div>
    </div>
  );
}
